import { BUTTON_VARIANTS, RadioGroup } from "@cdkglobal/radial";
import { RowData, RowDataReducerState } from "../../../../types";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { StyledExportRecordsDialog } from "../../../../utils/styled";
import { useState } from "react";
import { useUserContext } from "../../../../contexts/UserContext";
import { exportRecords, getSelectedId } from "../../helpers";
import { ampli } from "../../../../ampli";
import { useIntl } from "react-intl";

export default function ExportRecordsDialog({
  rowDataList,
  rowDataReducerState,
}: Readonly<{
  rowDataList: RowData[];
  rowDataReducerState: RowDataReducerState;
}>): JSX.Element {
  const intl = useIntl();
  const { selected, iamUserState } = useUserContext();
  const selectedIdLabel = getSelectedId(selected);
  const { isExportDialogOpen, setIsExportDialogOpen } = useUIStateContext();
  const [checkedOption, setCheckedOption] = useState("");

  const handleDialogClose = () => {
    setIsExportDialogOpen(false);
    ampli.closeButtonClick({ reason: "User closed export dialog" });
  };

  const handleFormatOptionSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCheckedOption(event.target.value);
  };

  const formatMap: Record<string, "Excel (.xlsx)" | "CSV (.csv)"> = {
    excel: "Excel (.xlsx)",
    csv: "CSV (.csv)",
  };

  const handleExportRecords = () => {
    const fileName = `${selected.setup.label}_${selectedIdLabel}_${
      new Date().toISOString().split("T")[0]
    }`
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, "-");

    exportRecords({
      rowDataList,
      fileName,
      format: checkedOption,
      selected,
      rowDataReducerState,
      iamUserState,
    });
    ampli.downloadButtonClick({
      "Download Records": true,
      exportType: formatMap[checkedOption],
    });
    handleDialogClose();
  };

  return (
    <StyledExportRecordsDialog
      id="export-records"
      dataTestId="export-records"
      buttonsProps={[
        {
          id: "export-records-cancel",
          onClick: handleDialogClose,
          text: intl.formatMessage({
            id: "button.cancel",
            defaultMessage: "Cancel",
          }),
          variant: BUTTON_VARIANTS.TEXT,
        },
        {
          id: "export-records",
          onClick: handleExportRecords,
          text: intl.formatMessage({
            id: "button.download",
            defaultMessage: "Download",
          }),
          variant: BUTTON_VARIANTS.PRIMARY,
          isDisabled: checkedOption === "",
        },
      ]}
      isOpen={isExportDialogOpen}
      onClose={handleDialogClose}
      title={intl.formatMessage({
        id: "dialog.exportRecordsTitle",
        defaultMessage: "Export Records?",
      })}
    >
      <RadioGroup
        dataTestId="export-records-format"
        id="export-records-format"
        label={
          <>
            {intl.formatMessage({
              id: "label.exportFormat",
              defaultMessage: "Select export format",
            })}
            <p>
              Note: Export only includes records matching current search (
              {rowDataList.length} records).
            </p>
          </>
        }
        labelPosition="after"
        name="contact-method"
        onChange={handleFormatOptionSelect}
        options={[
          {
            ariaLabel: intl.formatMessage({
              id: "radio.exportAsExcel",
              defaultMessage: "Export as Excel Spreadsheet",
            }),
            id: "excel-option",
            label: "Excel (.xlsx)",
            value: "excel",
          },
          {
            ariaLabel: intl.formatMessage({
              id: "radio.exportAsCSV",
              defaultMessage: "Export as CSV",
            }),
            id: "csv-option",
            label: "CSV (.csv)",
            value: "csv",
          },
        ]}
        radioSize="small"
        value={checkedOption}
      />
    </StyledExportRecordsDialog>
  );
}
